import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { GET_API_URL, GOOGLE_RECAPTCHA_ENABLE } from 'shared/config'
import {
  interceptorsRequestError,
  interceptorsRequestSuccess,
} from 'entities/Auth/lib/interceptorsRequest'
import {
  interceptorsApiError,
  interceptorsResponseError,
  interceptorsResponseLogError,
  interceptorsResponseSuccess,
} from 'entities/Auth/lib/interceptorsResponse'
import { getAssumeToken } from 'entities/Auth/lib/setToken'
import { CAPTCHA_RESPONSE_CODES } from 'entities/Auth/lib/captchaErrors'

export const baseApi = axios.create({
  baseURL: GET_API_URL(),
  headers: {
    'Content-type': 'application/json',
    'Salesmsg-App': 'react',
  },
})

const handleInterceptorsResponseSuccess = (config: AxiosResponse): AxiosResponse => {
  const isAssume = Boolean(getAssumeToken())

  return interceptorsResponseSuccess(config, isAssume)
}

/**
 * Merges the current captcha params into a request body for replay. Returns false when the body
 * is not a JSON object we can safely reconstruct (FormData, URLSearchParams, a custom transform),
 * so the caller rejects with the original captcha error rather than a SyntaxError from this
 * interceptor. The interceptor is global, so a future captcha-protected request with a non-JSON
 * body must not be turned into a parse failure.
 */
const mergeCaptchaParams = (config: AxiosError['config'], params?: object) => {
  if (!config) return false

  const { data } = config

  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false

      config.data = { ...parsed, ...params }

      return true
    } catch {
      return false
    }
  }

  if (data && typeof data === 'object' && !Array.isArray(data) && !(data instanceof FormData)) {
    config.data = { ...data, ...params }

    return true
  }

  return false
}

export const interceptorsResponseReCaptchaError = async (
  error: AxiosError<{ message: string }>
) => {
  if (!GOOGLE_RECAPTCHA_ENABLE) return Promise.reject(error)

  const code = error.response?.data?.message
  const isVisualChallengeNeeded = code === CAPTCHA_RESPONSE_CODES.visualChallengeNeeded
  const config = error.config

  // Strict routes make a v3 token single-use, so a rejected token is retryable with a fresh mint
  // for the same action. Once only: a second rejection is not transient.
  if (config && code === CAPTCHA_RESPONSE_CODES.tokenInvalid && !config.reCaptchaTokenRetried) {
    config.reCaptchaTokenRetried = true

    // The action is the one snapshotted when this request was built, not whatever the shared
    // store holds now — another auth flow may have moved it on since.
    const token = await config.reCaptchaStore?.refreshCaptchaToken(config.reCaptchaAction)

    if (!token) return Promise.reject(error)

    if (!mergeCaptchaParams(config, config.reCaptchaStore?.params)) return Promise.reject(error)

    return baseApi.request(config)
  }

  if (config && isVisualChallengeNeeded) {
    if (config.reCaptchaStore?.reCaptchaV2Store.error) {
      return Promise.reject(new Error(config.reCaptchaStore?.reCaptchaV2Store.error))
    }

    if (config.reCaptchaStore?.visualChallengeToken) return Promise.reject(error)

    await config.reCaptchaStore?.setVisualChallenge()

    if (!mergeCaptchaParams(config, config.reCaptchaStore?.params)) return Promise.reject(error)

    return baseApi.request(config)
  }

  return Promise.reject(error)
}

baseApi.interceptors.response.use(null, interceptorsApiError)
baseApi.interceptors.request.use(interceptorsRequestSuccess, interceptorsRequestError)
baseApi.interceptors.response.use(null, interceptorsResponseLogError)
baseApi.interceptors.response.use(null, interceptorsResponseReCaptchaError)
baseApi.interceptors.response.use(handleInterceptorsResponseSuccess, interceptorsResponseError)
